<script setup lang="ts">
/**
 * Число, которое «перелистывается» при изменении: вверх, когда стало больше,
 * вниз — когда меньше.
 *
 * Одинаковое движение в обе стороны читалось бы как подёргивание, а не как
 * «стало дороже». Поэтому направление считается от ПРЕДЫДУЩЕГО значения, а не
 * от того, что нажали: и цена, и количество меняются не только по кнопке рядом
 * (добавки, пересчёт комбо, синхронизация корзины с сервером).
 *
 * Используется и в счётчике количества, и в цене товара — чтобы движение
 * в карточке было одно и то же, а не две похожие анимации из разных мест.
 */
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Значение. Число — потому что от него же считается направление. */
    value: number;
    /** Приписка справа: «₽», «шт» и т.п. Не перелистывается — она не меняется. */
    suffix?: string;
  }>(),
  { suffix: '' },
);

const direction = ref<'up' | 'down'>('up');

watch(
  () => props.value,
  (next, previous) => {
    direction.value = next >= (previous ?? next) ? 'up' : 'down';
  },
);

/**
 * Ключ перехода — по показанному значению, а не по props.value: если формат
 * округляет (0.999 → «1»), два разных числа дают одну и ту же надпись, и
 * перелистывать нечего.
 */
const displayed = computed(() => props.value);
</script>

<template>
  <span class="rolling-value">
    <Transition :name="`rolling-value-${direction}`" mode="out-in">
      <span :key="displayed" class="rolling-value__cell">
        <slot :value="displayed">{{ displayed }}</slot>
      </span>
    </Transition>
    <span v-if="suffix" class="rolling-value__suffix">{{ suffix }}</span>
  </span>
</template>

<style scoped lang="scss">
.rolling-value {
  // inline-flex, а не inline: ячейка уезжает вверх/вниз, и строчный контекст
  // подтягивал бы за ней базовую линию соседнего текста
  @apply inline-flex items-center;
  // Значение уезжает за границы во время перелистывания — прячем «хвосты»
  overflow: hidden;
  // Между значением и припиской переноса быть не должно
  white-space: nowrap;
}

/*
  Длительность короткая: и счётчик, и добавки жмут очередями, и долгая
  анимация начала бы отставать от нажатий.
*/
.rolling-value-up-enter-active,
.rolling-value-up-leave-active,
.rolling-value-down-enter-active,
.rolling-value-down-leave-active {
  transition:
    transform 0.16s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.16s ease;
}

.rolling-value-up-enter-from {
  transform: translateY(70%);
  opacity: 0;
}

.rolling-value-up-leave-to {
  transform: translateY(-70%);
  opacity: 0;
}

.rolling-value-down-enter-from {
  transform: translateY(-70%);
  opacity: 0;
}

.rolling-value-down-leave-to {
  transform: translateY(70%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .rolling-value-up-enter-active,
  .rolling-value-up-leave-active,
  .rolling-value-down-enter-active,
  .rolling-value-down-leave-active {
    transition: none;
  }
}
</style>
